// Production URL for affiliate links, SEO canonical URLs, and social sharing
export const PRODUCTION_URL = 'https://lombrici.nl';

/**
 * Client Subdomains Feature Flag
 * 
 * When FALSE (recommended for beta):
 * - All clients login via app.lombrici.nl/dashboard
 * - No {slug}.lombrici.nl routing
 * - Simpler DNS setup
 * 
 * When TRUE:
 * - Client-specific branding via {slug}.lombrici.nl
 * - Requires DNS wildcard configuration
 * - Premium feature for agencies
 */
export const CLIENT_SUBDOMAINS_ENABLED = false;

// Domain architecture for multi-tenant system
export const DOMAINS = {
  /** Main marketing/catalog site */
  PRODUCTION: 'lombrici.nl',
  /** Operator HQ (Command Center) - branded as "HQ Lombrici Core" */
  HQ: 'hq.lombrici.nl',
  /** Platform (main app for users) */
  APP: 'app.lombrici.nl',
  /** Pattern for client sub-account subdomains: {slug}.lombrici.nl */
  CLIENT_PATTERN: /^([a-z0-9-]+)\.lombrici\.nl$/,
  /** Reserved subdomains that are NOT client sub-accounts */
  RESERVED_SUBDOMAINS: ['www', 'hq', 'app', 'api', 'mail', 'smtp', 'ftp', 'support', 'noreply'],
  /** Niche funnel subdomains - affiliate landing pages */
  NICHE_FUNNELS: ['consultants', 'operators', 'agencies', 'saas', 'local', 'scaling', 'enterprise'] as const,
} as const;

export type NicheFunnelSlug = typeof DOMAINS.NICHE_FUNNELS[number];

export type DomainType = 'main' | 'hq' | 'app' | 'client' | 'niche';

export interface ParsedDomain {
  type: DomainType;
  subdomain: string | null;
  /** For niche funnels, the specific niche slug */
  nicheSlug: NicheFunnelSlug | null;
}

/**
 * Parse the current hostname to determine domain type and extract subdomain
 * 
 * Domain Types:
 * - 'main': lombrici.nl (public marketing + catalog)
 * - 'hq': hq.lombrici.nl (operator command center)
 * - 'app': app.lombrici.nl (platform for users) or localhost/lovable.app
 * - 'niche': consultants.lombrici.nl, operators.lombrici.nl, etc. (affiliate funnels)
 * - 'client': {slug}.lombrici.nl (client sub-account / consultant / agency white-label)
 */
export function parseSubdomain(hostname: string): ParsedDomain {
  // Local development & Lovable preview → treat as 'app' (platform)
  if (
    hostname === 'localhost' ||
    hostname.includes('lovable.app') ||
    hostname.includes('lovable.dev') ||
    hostname.includes('lovableproject.com')
  ) {
    return { type: 'app', subdomain: null, nicheSlug: null };
  }
  
  // HQ domain (operator command center)
  // DEPRECATED: hq.lombrici.nl is no longer used - HQ is now at app.lombrici.nl/hq
  // Treat HQ as 'app' so AppRoutes renders and redirects to /hq path
  if (hostname === DOMAINS.HQ) {
    return { type: 'app', subdomain: null, nicheSlug: null };
  }
  
  // App domain (platform for users)
  if (hostname === DOMAINS.APP) {
    return { type: 'app', subdomain: null, nicheSlug: null };
  }
  
  // Main marketing site (lombrici.nl, www.lombrici.nl)
  if (hostname === DOMAINS.PRODUCTION || hostname === `www.${DOMAINS.PRODUCTION}`) {
    return { type: 'main', subdomain: null, nicheSlug: null };
  }
  
  // Subdomain pattern: {slug}.lombrici.nl
  const match = hostname.match(DOMAINS.CLIENT_PATTERN);
  if (match) {
    const slug = match[1];
    
    // Check if it's a reserved subdomain
    if ((DOMAINS.RESERVED_SUBDOMAINS as readonly string[]).includes(slug)) {
      return { type: 'main', subdomain: null, nicheSlug: null };
    }
    
    // Check if it's a niche funnel subdomain
    if ((DOMAINS.NICHE_FUNNELS as readonly string[]).includes(slug)) {
      return { type: 'niche', subdomain: slug, nicheSlug: slug as NicheFunnelSlug };
    }
    
    // Otherwise it's a client sub-account (consultant or agency)
    return { type: 'client', subdomain: slug, nicheSlug: null };
  }
  
  // Default to main
  return { type: 'main', subdomain: null, nicheSlug: null };
}

/**
 * Get the URL for a client sub-account subdomain
 */
export function getClientSubdomainUrl(slug: string): string {
  return `https://app.${slug}.${DOMAINS.PRODUCTION}`;
}

/**
 * Check if current environment is development
 */
export function isDevelopment(): boolean {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  return (
    hostname === 'localhost' ||
    hostname.includes('lovable.app') ||
    hostname.includes('lovable.dev') ||
    hostname.includes('lovableproject.com')
  );
}

/**
 * Get the URL for the app platform (app.lombrici.nl)
 * In development, returns relative path for local testing
 */
export function getAppUrl(path: string = ''): string {
  return isDevelopment() 
    ? path 
    : `https://${DOMAINS.APP}${path}`;
}

/**
 * Get the URL for the marketing site (lombrici.nl)
 * In development, returns relative path for local testing
 */
export function getMarketingUrl(path: string = ''): string {
  return isDevelopment() 
    ? path 
    : `https://${DOMAINS.PRODUCTION}${path}`;
}

/**
 * Get the URL for the HQ platform (hq.lombrici.nl)
 * In development, returns relative path for local testing
 */
export function getHQUrl(path: string = ''): string {
  return isDevelopment() 
    ? `/admin${path}` 
    : `https://${DOMAINS.HQ}${path}`;
}
